import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { 
  Plus, 
  ExternalLink, 
  Edit2, 
  Trash2, 
  FileText,
  Loader2,
  Search,
  Filter,
  Eye,
  RefreshCw,
  Copy,
  Link2,
  Sparkles
} from 'lucide-react';

const DECK_STATUSES = [
  { id: 'concept', label: 'Concept', color: '#6B7280' },
  { id: 'presented', label: 'Presented', color: '#3B82F6' },
  { id: 'samples_requested', label: 'Samples Requested', color: '#F59E0B' },
  { id: 'approved', label: 'Approved', color: '#10B981' },
  { id: 'closed', label: 'Closed', color: '#1F2937' },
];

export default function DeckLibrary() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { get, post, put, del, loading } = useApi();
  
  const [decks, setDecks] = useState([]);
  const [publishedDecks, setPublishedDecks] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('published');
  
  const [formData, setFormData] = useState({
    title: '',
    deck_url: '',
    opportunity_id: '',
    status: 'concept',
    internal_notes: '',
  });
  const [syncing, setSyncing] = useState(false);

  const fetchDecks = async () => {
    try {
      const data = await get('/deck-library');
      setDecks(data);
    } catch (error) {
      console.error('Failed to fetch deck library:', error);
    }
  };

  const fetchPublishedDecks = async () => {
    try {
      const data = await get('/style-archive');
      setPublishedDecks(data);
    } catch (error) {
      console.error('Failed to fetch published decks:', error);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const data = await get('/opportunities');
      setOpportunities(data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    }
  };

  useEffect(() => {
    fetchDecks();
    fetchPublishedDecks();
    fetchOpportunities();
  }, []);

  const copyToClipboard = (text) => {
    // If text doesn't start with http, prepend the origin
    const fullUrl = text.startsWith('http') ? text : `${window.location.origin}${text}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard!');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      deck_url: '',
      opportunity_id: '',
      status: 'concept',
      internal_notes: '',
    });
    setSelectedDeck(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert "none" to empty string for API
      const submitData = {
        ...formData,
        opportunity_id: formData.opportunity_id === 'none' ? '' : formData.opportunity_id
      };
      if (selectedDeck) {
        await put(`/deck-library/${selectedDeck.id}`, submitData);
        toast.success('Deck entry updated');
      } else {
        await post('/deck-library', submitData);
        toast.success('Deck entry added');
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchDecks();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (deck) => {
    setSelectedDeck(deck);
    setFormData({
      title: deck.title,
      deck_url: deck.deck_url,
      opportunity_id: deck.opportunity_id || 'none',
      status: deck.status,
      internal_notes: deck.internal_notes || '',
    });
    setCreateDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deck entry?')) return;
    try {
      await del(`/deck-library/${id}`);
      toast.success('Deck entry deleted');
      fetchDecks();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (statusId) => {
    const status = DECK_STATUSES.find(s => s.id === statusId);
    if (!status) return null;
    return (
      <Badge 
        className="rounded-sm text-xs font-medium"
        style={{ backgroundColor: status.color, color: '#fff' }}
      >
        {status.label}
      </Badge>
    );
  };

  const getAccountName = (oppId) => {
    const opp = opportunities.find(o => o.id === oppId);
    return opp?.account_name || 'Global';
  };

  const filteredDecks = decks.filter(deck => {
    const matchesSearch = 
      deck.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getAccountName(deck.opportunity_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || deck.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const canEdit = (deck) => {
    if (isAdmin) return true;
    return deck.created_by === user?.id;
  };

  const canDelete = (deck) => {
    if (isAdmin) return true;
    return deck.created_by === user?.id;
  };

  const handleSyncToPipeline = async () => {
    setSyncing(true);
    try {
      const result = await post('/deck-library/sync-to-pipeline');
      toast.success(result.message);
      fetchDecks();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="deck-library-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Style Archive</h1>
            <p className="text-muted-foreground">
              Published vision decks and design references
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button 
                variant="outline" 
                onClick={handleSyncToPipeline}
                disabled={syncing}
                className="rounded-sm"
                data-testid="sync-pipeline-btn"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Sync to Pipeline
              </Button>
            )}
            
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              setCreateDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="add-deck-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Reference
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="deck-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">
                  {selectedDeck ? 'Edit Deck Entry' : 'Add Deck to Library'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Deck Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Four Seasons Miami - Spring Collection"
                    required
                    className="rounded-sm"
                    data-testid="deck-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Hosted Deck URL *</Label>
                  <Input
                    value={formData.deck_url}
                    onChange={(e) => setFormData({ ...formData, deck_url: e.target.value })}
                    placeholder="https://canva.com/design/..."
                    required
                    type="url"
                    className="rounded-sm"
                    data-testid="deck-url"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Associated Account</Label>
                  <Select
                    value={formData.opportunity_id}
                    onValueChange={(value) => setFormData({ ...formData, opportunity_id: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="deck-account">
                      <SelectValue placeholder="Select account (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Global / No Account</SelectItem>
                      {opportunities.map((opp) => (
                        <SelectItem key={opp.id} value={opp.id}>
                          {opp.account_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="deck-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DECK_STATUSES.map((status) => (
                        <SelectItem key={status.id} value={status.id}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Internal Notes</Label>
                  <Textarea
                    value={formData.internal_notes}
                    onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                    placeholder="Notes for internal reference..."
                    rows={3}
                    className="rounded-sm resize-none"
                    data-testid="deck-notes"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => {
                    setCreateDialogOpen(false);
                    resetForm();
                  }} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button type="submit" className="rounded-sm" disabled={loading} data-testid="save-deck-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (selectedDeck ? 'Update' : 'Add')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Tabs for Published Decks vs References */}
        <div className="flex items-center gap-2 border-b">
          <button
            onClick={() => setActiveTab('published')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'published' 
                ? 'border-[#B89B5E] text-[#B89B5E]' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sparkles className="h-4 w-4 inline mr-2" />
            Published Vision Decks ({publishedDecks.length})
          </button>
          <button
            onClick={() => setActiveTab('references')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'references' 
                ? 'border-[#B89B5E] text-[#B89B5E]' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Design References ({decks.length})
          </button>
        </div>

        {/* Published Vision Decks Tab */}
        {activeTab === 'published' && (
          <Card className="rounded-sm border">
            <CardContent className="p-0">
              {publishedDecks.length === 0 ? (
                <div className="py-16 text-center">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-2">No published vision decks yet</p>
                  <p className="text-sm text-muted-foreground">
                    Publish a vision deck to see it here with shareable links
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                  {publishedDecks.map((entry) => (
                    <Card key={entry.id} className="rounded-sm overflow-hidden hover:shadow-md transition-shadow">
                      {entry.thumbnail_url ? (
                        <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
                          <img 
                            src={entry.thumbnail_url} 
                            alt={entry.deck_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gradient-to-br from-[#B89B5E]/20 to-[#B89B5E]/5 flex items-center justify-center">
                          <FileText className="h-12 w-12 text-[#B89B5E]/50" />
                        </div>
                      )}
                      <CardContent className="p-4 space-y-3">
                        <div>
                          <h3 className="font-medium text-sm line-clamp-1">{entry.deck_name}</h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            Published by {entry.published_by_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(entry.published_at)}
                          </p>
                        </div>
                        
                        {/* Working Link Display */}
                        <div className="p-2 bg-green-50 dark:bg-green-950/30 rounded border border-green-200 dark:border-green-800">
                          <p className="text-[10px] font-medium text-green-700 dark:text-green-400 mb-1">SHAREABLE LINK</p>
                          <code className="text-xs text-green-800 dark:text-green-300 break-all line-clamp-1">
                            {window.location.origin}{entry.public_url}
                          </code>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 rounded-sm text-xs"
                            onClick={() => window.open(entry.public_url, '_blank')}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm"
                            onClick={() => copyToClipboard(entry.public_url)}
                            title="Copy working link"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            className="rounded-sm bg-[#B89B5E] hover:bg-[#A08050] text-xs"
                            onClick={() => navigate(`/email-designer`)}
                          >
                            <Link2 className="h-3 w-3 mr-1" />
                            Email
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Design References Tab */}
        {activeTab === 'references' && (
          <>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title or account..."
              className="pl-10 rounded-sm"
              data-testid="deck-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-sm" data-testid="deck-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {DECK_STATUSES.map((status) => (
                <SelectItem key={status.id} value={status.id}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Deck List */}
        <Card className="rounded-sm border">
          <CardContent className="p-0">
            {filteredDecks.length === 0 ? (
              <div className="py-16 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'No decks match your filters' 
                    : 'No reference decks yet'}
                </p>
                {!searchQuery && statusFilter === 'all' && (
                  <Button onClick={() => setCreateDialogOpen(true)} className="rounded-sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Reference
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Account</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDecks.map((deck) => (
                      <tr 
                        key={deck.id} 
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer"
                        onClick={() => navigate(`/deck-library/${deck.id}`)}
                        data-testid={`deck-row-${deck.id}`}
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-sm hover:underline">{deck.title}</p>
                            {deck.internal_notes && (
                              <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                                {deck.internal_notes}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm">{getAccountName(deck.opportunity_id)}</span>
                        </td>
                        <td className="py-4 px-4">
                          {getStatusBadge(deck.status)}
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(deck.created_at).split(',')[0]}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/deck-library/${deck.id}`)}
                              className="rounded-sm"
                              data-testid={`view-deck-${deck.id}`}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(deck.deck_url, '_blank')}
                              className="rounded-sm"
                              data-testid={`open-deck-${deck.id}`}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Open
                            </Button>
                            {canEdit(deck) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(deck)}
                                className="rounded-sm"
                                data-testid={`edit-deck-${deck.id}`}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                            )}
                            {canDelete(deck) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(deck.id)}
                                className="rounded-sm text-destructive hover:text-destructive"
                                data-testid={`delete-deck-${deck.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
